import React, { useState, useEffect, useCallback } from 'react';
import { BellIcon, XMarkIcon, Cog6ToothIcon, ExclamationTriangleIcon, InformationCircleIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import { realtimeNotificationService, Notification } from '../../services/realtime';
import { useNavigate } from 'react-router-dom';
import { apiService } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';

interface NotificationManagerProps {
  userId: string;
  variant?: 'sidebar' | 'header';
}

const NotificationManager: React.FC<NotificationManagerProps> = ({ userId, variant = 'header' }) => {
  const [notifications, setNotifications] = useState<any[]>([]);

  const [showNotificationCenter, setShowNotificationCenter] = useState(false);
  const [unreadCount, setUnreadCount] = useState({ events: 0, updates: 0, total: 0 });
  const [activeTab, setActiveTab] = useState<'events' | 'updates'>('events');
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();

  // Load notifications function
  const loadNotifications = useCallback(async () => {
    console.log('🔍 loadNotifications called');
    console.log('🔍 userId:', userId);
    console.log('🔍 activeTab:', activeTab);
    
    if (!userId) {
      console.log('❌ No userId, returning early');
      return;
    }
    
    setLoading(true);
    try {
      // Load notifications for current tab
      const category = activeTab === 'events' ? 'events' : 'updates';
      console.log('🔍 Loading notifications for category:', category);
      
              const response = await apiService.getNotifications(userId, { category });
        console.log('🔍 API response:', response);
        console.log('🔍 Raw notifications:', response.notifications);
        
        // Log each notification to see what's happening
        (response.notifications || []).forEach((item: any, index: number) => {
          console.log(`🔍 Notification ${index}:`, {
            id: item.id,
            notification_content: item.notification_content,
            has_content: !!item.notification_content,
            category: item.notification_content?.category,
            expected_category: category
          });
        });
      
      // Transform the response to flatten the nested notification_content
      const transformedNotifications = (response.notifications || []).map((item: any) => {
        
        // Handle case where notification_content might be null/undefined
        if (!item.notification_content) {
          console.log('⚠️ Item missing notification_content:', item);
          return null;
        }
        
        const transformed = {
          id: item.notification_content.id || item.id,
          notification_type: item.notification_content.notification_type,
          title: item.notification_content.title || 'No Title',
          message: item.notification_content.message || 'No Message',
          priority: item.notification_content.priority || 'normal',
          category: item.notification_content.category || 'updates',
          action_url: item.notification_content.action_url,
          action_text: item.notification_content.action_text,
          metadata: item.notification_content.metadata,
          created_at: item.notification_content.created_at,
          received_at: item.received_at,
          viewed_at: item.viewed_at,
          read_at: item.read_at,
          clicked_at: item.clicked_at,
          archived_at: item.archived_at,
        };
        return transformed;
      }).filter(Boolean) // Remove null items
        .filter((notification: any) => {
          // Filter by category on the frontend
          const matchesCategory = notification.category === category;
          console.log(`🔍 Filtering: ${notification.title} (${notification.category}) matches ${category}: ${matchesCategory}`);
          return matchesCategory;
        });
      
      console.log('🔍 Transformed notifications:', transformedNotifications);
      setNotifications(transformedNotifications);
      console.log('🔍 Notifications state updated');
    } catch (error) {
      console.error('❌ Failed to load notifications:', error);
    } finally {
      setLoading(false);
      console.log('🔍 Loading finished');
    }
  }, [userId, activeTab]);

  // Load unread count function
  const loadUnreadCount = useCallback(async () => {
    console.log('🔍 loadUnreadCount called');
    console.log('🔍 userId:', userId);
    
    if (!userId) {
      console.log('❌ No userId, returning early');
      return;
    }
    
    try {
      console.log('🔍 Calling getUnreadCount...');
      const countResponse = await apiService.getUnreadCount(userId);
      console.log('🔍 Unread count response:', countResponse);
      
      setUnreadCount(countResponse);
      console.log('🔍 Unread count state updated');
    } catch (error) {
      console.error('❌ Failed to load unread count:', error);
    }
  }, [userId]);

  // Load notifications and unread count on mount and tab change
  useEffect(() => {
    // Only load if userId is available
    if (!userId) return;
    
    // Add debouncing to prevent rapid API calls
    const timeoutId = setTimeout(() => {
      loadNotifications();
      loadUnreadCount();
    }, 100); // 100ms debounce

    return () => clearTimeout(timeoutId);
  }, [userId, loadNotifications, loadUnreadCount]);

  // Connect to real-time notifications
  useEffect(() => {
    if (userId) {
      realtimeNotificationService.connect(userId);
    }



    const handleNewNotification = (notification: Notification) => {
      setNotifications(prev => [notification, ...prev]);
      // Update unread count for the appropriate category
      setUnreadCount(prev => ({
        ...prev,
        updates: prev.updates + 1, // Default to updates for realtime notifications
        total: prev.total + 1
      }));
    };

    // Subscribe to new notifications
    const unsubscribeNotification = realtimeNotificationService.onNotification(handleNewNotification);

    return () => {
      unsubscribeNotification();
      realtimeNotificationService.disconnect();
    };
  }, [userId]);

  const markNotificationsAsViewed = useCallback(async () => {
    if (userId && notifications.length > 0) {
      try {
        // Mark all visible notifications as viewed (if not already viewed)
        const unviewedNotifications = notifications.filter(n => !n.viewed_at);
        await Promise.all(
          unviewedNotifications.map(notification => 
            apiService.markNotificationViewed(userId, notification.id)
          )
        );
      } catch (error) {
        console.error('Failed to mark notifications as viewed:', error);
      }
    }
  }, [userId, notifications]);

  // Mark notifications as viewed when modal opens
  useEffect(() => {
    if (showNotificationCenter && userId && notifications.length > 0) {
      markNotificationsAsViewed();
    }
  }, [showNotificationCenter, userId, notifications, markNotificationsAsViewed]);



  const handleMarkRead = useCallback(async (id: string) => {
    if (userId) {
      try {
        // Mark as read on the server
        await apiService.markNotificationRead(userId, id);
        
        // Refresh data from backend to get updated state
        await loadNotifications();
        await loadUnreadCount();
      } catch (error) {
        console.error('Failed to mark notification as read:', error);
      }
    }
  }, [userId, loadNotifications, loadUnreadCount]);

  const handleMarkAllRead = useCallback(async () => {
    console.log('🔍 handleMarkAllRead called');
    console.log('🔍 userId:', userId);
    console.log('🔍 activeTab:', activeTab);
    
    if (userId) {
      try {
        console.log('🔍 Calling markAllNotificationsRead...');
        // Mark all as read on the server
        await apiService.markAllNotificationsRead(userId, activeTab);
        console.log('🔍 markAllNotificationsRead succeeded');
        
        console.log('🔍 Refreshing notifications...');
        // Refresh data from backend to get updated state
        await loadNotifications();
        console.log('🔍 loadNotifications completed');
        
        console.log('🔍 Refreshing unread count...');
        await loadUnreadCount();
        console.log('🔍 loadUnreadCount completed');
        
        console.log('🔍 handleMarkAllRead completed successfully');
      } catch (error) {
        console.error('❌ Failed to mark all notifications as read:', error);
      }
    } else {
      console.log('❌ No userId available');
    }
  }, [userId, activeTab, loadNotifications, loadUnreadCount]);

  const handleClearAll = useCallback(async () => {
    if (userId) {
      try {
        // Clear notifications on the server
        await apiService.clearNotifications(userId, activeTab);
        
        // Refresh data from backend to get updated state
        await loadNotifications();
        await loadUnreadCount();
      } catch (error) {
        console.error('Failed to clear notifications:', error);
      }
    }
  }, [userId, activeTab, loadNotifications, loadUnreadCount]);





  // Sidebar variant - simple text with notification count
  if (variant === 'sidebar') {
    return (
      <>
        <button
          onClick={() => setShowNotificationCenter(!showNotificationCenter)}
          className="flex items-center justify-between w-full px-2 py-2 text-sm font-medium text-gray-600 hover:bg-gray-50 hover:text-gray-900 rounded-md group"
        >
          <div className="flex items-center">
            <BellIcon className="h-5 w-5 mr-3 text-gray-400 group-hover:text-gray-500" />
            <span>Notifications</span>
          </div>
          {unreadCount.total > 0 && (
            <div className="flex items-center justify-center h-5 w-5 bg-purple-500 text-white text-xs font-medium rounded-full">
              {unreadCount.total > 99 ? '99+' : unreadCount.total}
            </div>
          )}
        </button>

        {/* Notification Center */}
        {showNotificationCenter && (
          <div className="fixed inset-0 z-50 overflow-hidden">
            {/* Backdrop */}
            <div 
              className="absolute inset-0 bg-black bg-opacity-25 transition-opacity"
              onClick={() => setShowNotificationCenter(false)}
            />
            
            {/* Desktop Panel */}
            <div className="absolute bottom-0 left-0 mb-16 ml-64 w-80 bg-white rounded-lg shadow-xl border border-gray-200 hidden lg:block">
              <div className="flex flex-col h-full">
                {/* Header */}
                <div className="flex items-center justify-between p-4 border-b border-gray-200">
                  <h2 className="text-lg font-semibold text-gray-900">
                    Notifications
                  </h2>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => {
                        setShowNotificationCenter(false);
                        navigate('/settings?tab=notifications');
                      }}
                      className="text-gray-400 hover:text-gray-600 p-1 rounded-md hover:bg-gray-100 transition-colors"
                      title="Notification Settings"
                    >
                      <Cog6ToothIcon className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => setShowNotificationCenter(false)}
                      className="text-gray-400 hover:text-gray-600 p-1 rounded-md hover:bg-gray-100 transition-colors"
                    >
                      <XMarkIcon className="h-5 w-5" />
                    </button>
                  </div>
                </div>

                {/* Tabs */}
                <div className="border-b border-gray-200">
                  <div className="flex">
                    <button 
                      onClick={() => setActiveTab('events')}
                      className={`flex-1 px-4 py-3 text-sm font-medium border-b-2 ${
                        activeTab === 'events' 
                          ? 'text-gray-900 border-purple-500' 
                          : 'text-gray-500 hover:text-gray-700 border-transparent hover:border-gray-300'
                      }`}
                    >
                      Events
                      {unreadCount.events > 0 && (
                        <span className="ml-2 inline-flex items-center justify-center h-5 w-5 bg-purple-500 text-white text-xs font-medium rounded-full">
                          {unreadCount.events > 99 ? '99+' : unreadCount.events}
                        </span>
                      )}
                    </button>
                    <button 
                      onClick={() => setActiveTab('updates')}
                      className={`flex-1 px-4 py-3 text-sm font-medium border-b-2 ${
                        activeTab === 'updates' 
                          ? 'text-gray-900 border-purple-500' 
                          : 'text-gray-500 hover:text-gray-700 border-transparent hover:border-gray-300'
                      }`}
                    >
                      Updates
                      {unreadCount.updates > 0 && (
                        <span className="ml-2 inline-flex items-center justify-center h-5 w-5 bg-purple-500 text-white text-xs font-medium rounded-full">
                          {unreadCount.updates > 99 ? '99+' : unreadCount.updates}
                        </span>
                      )}
                    </button>
                  </div>
                </div>



                {/* Notifications List */}
                <div className="flex-1 overflow-y-auto max-h-96">
                  {notifications.length === 0 ? (
                    <div className="p-8 text-center text-gray-500">
                      <BellIcon className="h-8 w-8 mx-auto mb-3 text-gray-300" />
                      <p className="text-sm font-medium text-gray-900 mb-1">No notifications yet</p>
                      <p className="text-xs text-gray-500">You'll see real-time updates here</p>
                    </div>
                  ) : (
                    <>
                      {notifications.length > 8 && (
                        <div className="px-4 py-2 text-xs text-gray-500 text-center bg-gray-50 border-b border-gray-200">
                          Scroll to see {notifications.length - 8} more notifications
                        </div>
                      )}
                      <div className="divide-y divide-gray-200">
                        {notifications.map((notification) => (
                          <div key={notification.id} className={`p-4 hover:bg-gray-50 ${
                            !notification.read_at ? 'bg-blue-50 border-l-4 border-blue-400' : ''
                          }`}>
                            <div className="flex items-start space-x-3">
                              <div className="flex-shrink-0">
                                {!notification.read_at ? (
                                  <div className="h-2 w-2 bg-blue-500 rounded-full mt-2"></div>
                                ) : notification.priority === 'critical' ? (
                                  <ExclamationTriangleIcon className="h-5 w-5 text-red-500" />
                                ) : notification.priority === 'high' ? (
                                  <ExclamationTriangleIcon className="h-5 w-5 text-orange-500" />
                                ) : notification.priority === 'normal' ? (
                                  <InformationCircleIcon className="h-5 w-5 text-blue-500" />
                                ) : (
                                  <CheckCircleIcon className="h-5 w-5 text-green-500" />
                                )}
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-gray-900">{notification.title}</p>
                                <p className="text-sm text-gray-600 mt-1">{notification.message}</p>
                                <div className="flex items-center justify-between mt-2">
                                  <span className="text-xs text-gray-500">
                                    {new Date(notification.received_at || notification.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                  </span>
                                  <button
                                    onClick={() => handleMarkRead(notification.id)}
                                    className={`text-xs font-medium ${
                                      notification.read_at 
                                        ? 'text-gray-400 cursor-default' 
                                        : 'text-blue-600 hover:text-blue-700'
                                    }`}
                                    disabled={!!notification.read_at}
                                  >
                                    {notification.read_at ? 'Read' : 'Mark read'}
                                  </button>
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </div>

                {/* Actions - Bottom of Modal */}
                {notifications.length > 0 && (
                  <div className="px-4 py-3 bg-gray-50 border-t border-gray-200">
                    <div className="flex space-x-3">
                      <button
                        onClick={handleMarkAllRead}
                        className="flex items-center text-sm text-blue-600 hover:text-blue-700 font-medium"
                      >
                        <svg className="h-4 w-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        Mark all as read
                      </button>
                      <button
                        onClick={handleClearAll}
                        className="text-sm text-gray-600 hover:text-gray-700 font-medium"
                      >
                        Clear notifications
                      </button>
                    </div>
                  </div>
                )}


              </div>
            </div>
            
            {/* Mobile Panel */}
            <div className="absolute inset-x-0 bottom-0 bg-white rounded-t-lg shadow-xl border border-gray-200 lg:hidden">
              <div className="flex flex-col h-96">
                {/* Header */}
                <div className="flex items-center justify-between p-4 border-b border-gray-200">
                  <h2 className="text-lg font-semibold text-gray-900">
                    Notifications
                  </h2>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => {
                        setShowNotificationCenter(false);
                        navigate('/settings?tab=notifications');
                      }}
                      className="text-gray-400 hover:text-gray-600 p-1 rounded-md hover:bg-gray-100 transition-colors"
                      title="Notification Settings"
                    >
                      <Cog6ToothIcon className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => setShowNotificationCenter(false)}
                      className="text-gray-400 hover:text-gray-600 p-1 rounded-md hover:bg-gray-100 transition-colors"
                    >
                      <XMarkIcon className="h-5 w-5" />
                    </button>
                  </div>
                </div>

                {/* Tabs */}
                <div className="border-b border-gray-200">
                  <div className="flex">
                    <button 
                      onClick={() => setActiveTab('events')}
                      className={`flex-1 px-4 py-3 text-sm font-medium border-b-2 ${
                        activeTab === 'events' 
                          ? 'text-gray-900 border-purple-500' 
                          : 'text-gray-500 hover:text-gray-700 border-transparent hover:border-gray-300'
                      }`}
                    >
                      Events
                    </button>
                    <button 
                      onClick={() => setActiveTab('updates')}
                      className={`flex-1 px-4 py-3 text-sm font-medium border-b-2 ${
                        activeTab === 'updates' 
                          ? 'text-gray-900 border-purple-500' 
                          : 'text-gray-500 hover:text-gray-700 border-transparent hover:border-gray-300'
                      }`}
                    >
                      Updates
                    </button>
                  </div>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto">
                  {loading ? (
                    <div className="flex items-center justify-center h-32">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
                    </div>
                  ) : notifications.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-32 text-gray-500">
                      <BellIcon className="h-8 w-8 mb-2" />
                      <p className="text-sm">No notifications</p>
                      <p className="text-xs text-gray-400">You'll see real-time updates here</p>
                    </div>
                  ) : (
                    <>
                      <div className="p-4 space-y-3">
                        {notifications.map((notification) => (
                          <div key={notification.id} className="flex items-start space-x-3 p-3 hover:bg-gray-50 rounded-lg">
                            <div className="flex-shrink-0">
                              {notification.notification_content?.category === 'security' ? (
                                <ExclamationTriangleIcon className="h-5 w-5 text-red-500" />
                              ) : notification.notification_content?.category === 'system' ? (
                                <InformationCircleIcon className="h-5 w-5 text-blue-500" />
                              ) : (
                                <CheckCircleIcon className="h-5 w-5 text-green-500" />
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-900">
                                {notification.notification_content?.title || 'Notification'}
                              </p>
                              <p className="text-sm text-gray-500 mt-1">
                                {notification.notification_content?.message || 'No message available'}
                              </p>
                              <p className="text-xs text-gray-400 mt-1">
                                {new Date(notification.created_at).toLocaleString()}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </div>

                {/* Actions - Bottom of Modal */}
                {notifications.length > 0 && (
                  <div className="px-4 py-3 bg-gray-50 border-t border-gray-200">
                    <div className="flex space-x-3">
                      <button
                        onClick={handleMarkAllRead}
                        className="flex items-center text-sm text-blue-600 hover:text-blue-700 font-medium"
                      >
                        <svg className="h-4 w-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        Mark all as read
                      </button>
                      <button
                        onClick={handleClearAll}
                        className="text-sm text-gray-600 hover:text-gray-700 font-medium"
                      >
                        Clear notifications
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </>
    );
  }


  
  // Header variant - original bell icon with notification count
  return (
    <>
      {/* Notification Bell */}
      <div className="relative">
        <button
          onClick={() => setShowNotificationCenter(!showNotificationCenter)}
          className="flex items-center space-x-2 px-3 py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors w-full"
          title="Notifications"
        >
          <BellIcon className="h-5 w-5" />
          <span className="text-sm font-medium">Notifications</span>
          {unreadCount.total > 0 && (
            <span className="ml-auto h-5 w-5 bg-purple-500 text-white text-xs rounded-full flex items-center justify-center">
              {unreadCount.total > 99 ? '99+' : unreadCount.total}
            </span>
          )}
        </button>


      </div>

      {/* Notification Center */}
      {showNotificationCenter && (
        <div className="fixed inset-0 z-50 overflow-hidden">
          {/* Backdrop */}
          <div 
            className="absolute inset-0 bg-black bg-opacity-25 transition-opacity"
            onClick={() => setShowNotificationCenter(false)}
          />
          
          {/* Panel */}
          <div className="absolute left-0 top-0 mt-72 ml-64 w-80 bg-white rounded-lg shadow-xl border border-gray-200">
            <div className="flex flex-col h-full">
              {/* Header */}
              <div className="flex items-center justify-between p-4 border-b border-gray-200">
                <h2 className="text-lg font-semibold text-gray-900">
                  Notifications
                </h2>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => {
                      setShowNotificationCenter(false);
                      navigate('/settings?tab=notifications');
                    }}
                    className="text-gray-400 hover:text-gray-600 p-1 rounded-md hover:bg-gray-100 transition-colors"
                    title="Notification Settings"
                  >
                    <Cog6ToothIcon className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => setShowNotificationCenter(false)}
                    className="text-gray-400 hover:text-gray-600 p-1 rounded-md hover:bg-gray-100 transition-colors"
                  >
                    <XMarkIcon className="h-5 w-5" />
                  </button>
                </div>
              </div>

                            {/* Tabs */}
              <div className="border-b border-gray-200">
                <div className="flex">
                  <button 
                    key={`events-tab-${unreadCount.events}`}
                    onClick={() => setActiveTab('events')}
                    className={`flex-1 px-4 py-3 text-sm font-medium border-b-2 ${
                      activeTab === 'events' 
                        ? 'text-gray-900 border-purple-500' 
                        : 'text-gray-500 hover:text-gray-700 border-transparent hover:border-gray-300'
                    }`}
                  >
                    Events
                    {unreadCount.events > 0 && (
                      <span className="ml-2 inline-flex items-center justify-center h-5 w-5 bg-purple-500 text-white text-xs font-medium rounded-full">
                        {unreadCount.events > 99 ? '99+' : unreadCount.events}
                      </span>
                    )}
                  </button>
                  <button 
                    key={`updates-tab-${unreadCount.updates}`}
                    onClick={() => setActiveTab('updates')}
                    className={`flex-1 px-4 py-3 text-sm font-medium border-b-2 ${
                      activeTab === 'updates' 
                        ? 'text-gray-900 border-purple-500' 
                        : 'text-gray-500 hover:text-gray-700 border-transparent hover:border-gray-300'
                    }`}
                  >
                    Updates
                    {unreadCount.updates > 0 && (
                      <span className="ml-2 inline-flex items-center justify-center h-5 w-5 bg-purple-500 text-white text-xs font-medium rounded-full">
                        {unreadCount.updates > 99 ? '99+' : unreadCount.updates}
                      </span>
                    )}
                  </button>
                </div>
              </div>





              {/* Notifications List */}
              <div className="flex-1 overflow-y-auto max-h-96">
                {notifications.length === 0 ? (
                  <div className="p-8 text-center text-gray-500">
                    <BellIcon className="h-8 w-8 mx-auto mb-3 text-gray-300" />
                    <p className="text-sm font-medium text-gray-900 mb-1">No notifications yet</p>
                    <p className="text-xs text-gray-500">You'll see real-time updates here</p>
                  </div>
                ) : (
                  <>
                    {notifications.length > 8 && (
                      <div className="px-4 py-2 text-xs text-gray-500 text-center bg-gray-50 border-b border-gray-200">
                        Scroll to see {notifications.length - 8} more notifications
                      </div>
                    )}
                    <div className="divide-y divide-gray-200">
                      {notifications.map((notification) => (
                        <div key={notification.id} className={`p-4 hover:bg-gray-50 ${
                          !notification.read_at ? 'bg-blue-50 border-l-4 border-blue-400' : ''
                        }`}>
                          <div className="flex items-start space-x-3">
                            <div className="flex-shrink-0">
                              {!notification.read_at ? (
                                <div className="h-2 w-2 bg-blue-500 rounded-full mt-2"></div>
                              ) : notification.priority === 'critical' ? (
                                <ExclamationTriangleIcon className="h-5 w-5 text-red-500" />
                              ) : notification.priority === 'high' ? (
                                <ExclamationTriangleIcon className="h-5 w-5 text-orange-500" />
                              ) : notification.priority === 'normal' ? (
                                <InformationCircleIcon className="h-5 w-5 text-blue-500" />
                              ) : (
                                <CheckCircleIcon className="h-5 w-5 text-green-500" />
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-900">{notification.title}</p>
                              <p className="text-sm text-gray-600 mt-1">{notification.message}</p>
                              <div className="flex items-center justify-between mt-2">
                                <span className="text-xs text-gray-500">
                                  {new Date(notification.received_at || notification.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                </span>
                                <button
                                  onClick={() => handleMarkRead(notification.id)}
                                  className={`text-xs font-medium ${
                                    notification.read_at 
                                      ? 'text-gray-400 cursor-default' 
                                      : 'text-blue-600 hover:text-blue-700'
                                  }`}
                                  disabled={!!notification.read_at}
                                >
                                  {notification.read_at ? 'Read' : 'Mark read'}
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>

              {/* Actions - Bottom of Modal */}
              {notifications.length > 0 && (
                <div className="px-4 py-3 bg-gray-50 border-t border-gray-200">
                  <div className="flex space-x-3">
                    <button
                      onClick={handleMarkAllRead}
                      className="flex items-center text-sm text-blue-600 hover:text-blue-700 font-medium"
                    >
                      <svg className="h-4 w-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      Mark all as read
                    </button>
                    <button
                      onClick={handleClearAll}
                      className="text-sm text-gray-600 hover:text-gray-700 font-medium"
                    >
                      Clear notifications
                    </button>
                  </div>
                </div>
              )}


            </div>
          </div>
        </div>
      )}


    </>
  );
};

export default NotificationManager;
